import axios from 'axios';
import { api } from './api';

export interface CalendarEventDto {
  id: string;
  calendarId: string;
  name: string;
  eventDate: string;
  type: string;
  scope?: string;
  isRecurring: boolean;
}

export interface CalendarBreakDto {
  id: string;
  calendarId: string;
  dayOfWeek: string;
  startTime: string;
  endTime: string;
}

export interface CalendarDto {
  id: string;
  customerId?: string | null;
  name: string;
  isGlobal: boolean;
  workDays: string;
  workStartTime: string;
  workEndTime: string;
  events: CalendarEventDto[];
  breaks: CalendarBreakDto[];
}

export interface CreateCalendarDto {
  customerId: string;
  name: string;
  workDays: string;
  workStartTime: string;
  workEndTime: string;
}

export interface UpdateCalendarDto {
  name: string;
  workDays: string;
  workStartTime: string;
  workEndTime: string;
}

export interface CreateCalendarEventDto {
  name: string;
  eventDate: string;
  type: string;
  isRecurring: boolean;
}

export interface UpdateCalendarEventDto {
  name: string;
  eventDate: string;
  type: string;
  isRecurring: boolean;
}

export interface CreateCalendarBreakDto {
  dayOfWeek: string;
  startTime: string;
  endTime: string;
}

export interface UpdateCalendarBreakDto {
  dayOfWeek: string;
  startTime: string;
  endTime: string;
}

function extractApiErrorMessage(error: unknown, fallback: string): string {
  if (axios.isAxiosError(error)) {
    const d = error.response?.data;
    if (typeof d === 'string' && d.trim()) return d;
    if (d && typeof d === 'object') {
      const o = d as Record<string, unknown>;
      if (typeof o.detail === 'string') return o.detail;
      if (typeof o.title === 'string') return o.title;
      if (typeof o.message === 'string') return o.message;
    }
    if (error.response?.statusText) {
      return `${error.response.status}: ${error.response.statusText}`;
    }
  }
  if (error instanceof Error) return error.message;
  return fallback;
}

class CalendarService {
  async getCalendars(customerId: string): Promise<CalendarDto[]> {
    try {
      const response = await api.get<CalendarDto[]>('/calendars', {
        params: { customerId },
      });
      return response.data ?? [];
    } catch (error: unknown) {
      throw new Error(extractApiErrorMessage(error, 'Erro ao buscar calendários do cliente.'));
    }
  }

  async getGlobalCalendar(): Promise<CalendarDto | null> {
    try {
      const response = await api.get<CalendarDto>('/calendars/global');
      return response.data ?? null;
    } catch (error: unknown) {
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        return null;
      }
      throw new Error(extractApiErrorMessage(error, 'Erro ao buscar calendário global.'));
    }
  }

  async getCalendarById(id: string): Promise<CalendarDto | null> {
    try {
      const response = await api.get<CalendarDto>(`/calendars/${id}`);
      return response.data ?? null;
    } catch (error: unknown) {
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        return null;
      }
      throw new Error(extractApiErrorMessage(error, 'Erro ao buscar calendário.'));
    }
  }

  async getEffectiveCalendar(calendarId: string, customerId: string): Promise<CalendarDto> {
    try {
      const response = await api.get<CalendarDto>(`/calendars/${calendarId}/effective`, {
        params: { customerId },
      });
      return response.data;
    } catch (error: unknown) {
      throw new Error(extractApiErrorMessage(error, 'Erro ao buscar calendário efetivo.'));
    }
  }

  async createCalendar(data: CreateCalendarDto): Promise<string> {
    try {
      const response = await api.post<string>('/calendars', data);
      return typeof response.data === 'string' ? response.data : String(response.data);
    } catch (error: unknown) {
      throw new Error(extractApiErrorMessage(error, 'Erro ao criar calendário.'));
    }
  }

  async updateCalendar(id: string, data: UpdateCalendarDto): Promise<void> {
    try {
      await api.put(`/calendars/${id}`, data);
    } catch (error: unknown) {
      throw new Error(extractApiErrorMessage(error, 'Erro ao atualizar calendário.'));
    }
  }

  async deleteCalendar(id: string): Promise<void> {
    try {
      await api.delete(`/calendars/${id}`);
    } catch (error: unknown) {
      throw new Error(extractApiErrorMessage(error, 'Erro ao excluir calendário.'));
    }
  }

  async addCalendarEvent(calendarId: string, data: CreateCalendarEventDto): Promise<string> {
    try {
      const response = await api.post<string>(`/calendars/${calendarId}/events`, data);
      return typeof response.data === 'string' ? response.data : String(response.data);
    } catch (error: unknown) {
      throw new Error(extractApiErrorMessage(error, 'Erro ao criar evento.'));
    }
  }

  async updateCalendarEvent(
    calendarId: string,
    eventId: string,
    data: UpdateCalendarEventDto
  ): Promise<void> {
    try {
      await api.put(`/calendars/${calendarId}/events/${eventId}`, data);
    } catch (error: unknown) {
      throw new Error(extractApiErrorMessage(error, 'Erro ao atualizar evento.'));
    }
  }

  async deleteCalendarEvent(calendarId: string, eventId: string): Promise<void> {
    try {
      await api.delete(`/calendars/${calendarId}/events/${eventId}`);
    } catch (error: unknown) {
      throw new Error(extractApiErrorMessage(error, 'Erro ao excluir evento.'));
    }
  }

  async getBreaks(calendarId: string): Promise<CalendarBreakDto[]> {
    try {
      const response = await api.get<CalendarBreakDto[]>(`/calendars/${calendarId}/breaks`);
      return response.data ?? [];
    } catch (error: unknown) {
      throw new Error(extractApiErrorMessage(error, 'Erro ao buscar intervalos.'));
    }
  }

  async addBreak(calendarId: string, data: CreateCalendarBreakDto): Promise<string> {
    try {
      const response = await api.post<string>(`/calendars/${calendarId}/breaks`, data);
      return typeof response.data === 'string' ? response.data : String(response.data);
    } catch (error: unknown) {
      throw new Error(extractApiErrorMessage(error, 'Erro ao criar intervalo.'));
    }
  }

  async updateBreak(
    calendarId: string,
    breakId: string,
    data: UpdateCalendarBreakDto
  ): Promise<void> {
    try {
      await api.put(`/calendars/${calendarId}/breaks/${breakId}`, data);
    } catch (error: unknown) {
      throw new Error(extractApiErrorMessage(error, 'Erro ao atualizar intervalo.'));
    }
  }

  async deleteBreak(calendarId: string, breakId: string): Promise<void> {
    try {
      await api.delete(`/calendars/${calendarId}/breaks/${breakId}`);
    } catch (error: unknown) {
      throw new Error(extractApiErrorMessage(error, 'Erro ao excluir intervalo.'));
    }
  }
}

export const calendarService = new CalendarService();
